import * as React from "react"
import { cloneElement, useEffect, useMemo, useRef, useState } from "react"
import {
  GeoJSONSource,
  ImageSource,
  Map,
  Source,
  SourceSpecification,
} from "maplibre-gl"
import assert from "@/utils/assert"
import { deepEqual } from "@/utils/deepEqual"
import { useMapContext } from "@/components/maps/mapLibre/MapContext"

export type SourceProps = SourceSpecification & {
  id?: string
  children?: any
}

let sourceCounter = 0

function createSource(map: Map, id: string, props: SourceProps) {
  // @ts-ignore
  if (map.style && map.style._loaded) {
    const options = { ...props }
    delete options.id
    delete options.children
    // @ts-ignore
    map.addSource(id, options)
    return map.getSource(id)
  }
  return null
}

function updateSource(
  source: Source,
  props: SourceProps,
  prevProps: SourceProps,
) {
  assert(props.id === prevProps.id, "source id changed")
  assert(props.type === prevProps.type, "source type changed")

  let changedKey = ""
  let changedKeyCount = 0

  for (const key in props) {
    if (
      key !== "children" &&
      key !== "id" &&
      // @ts-ignore
      !deepEqual(prevProps[key], props[key])
    ) {
      changedKey = key
      changedKeyCount++
    }
  }

  if (!changedKeyCount) {
    return
  }

  const type = props.type

  if (type === "geojson") {
    ;(source as GeoJSONSource).setData(props.data)
  } else if (type === "image") {
    ;(source as ImageSource).updateImage({
      url: props.url,
      coordinates: props.coordinates,
    })
  } else {
    switch (changedKey) {
      case "coordinates":
        // @ts-ignore
        source.setCoordinates?.(props.coordinates)
        break
      case "url":
        // @ts-ignore
        source.setUrl?.(props.url)
        break
      case "tiles":
        // @ts-ignore
        source.setTiles?.(props.tiles)
        break
      default:
        console.warn(`Unable to update <Source> prop: ${changedKey}`)
    }
  }
}

export function Source(props: SourceProps) {
  const map = useMapContext()?.getMapInstance()
  const propsRef = useRef(props)
  const [, setStyleLoaded] = useState(0)

  const id = useMemo(() => props.id || `jsx-source-${sourceCounter++}`, [])

  useEffect(() => {
    if (map) {
      /* global setTimeout */
      const forceUpdate = () =>
        setTimeout(() => setStyleLoaded((version) => version + 1), 0)
      map.on("styledata", forceUpdate)
      forceUpdate()

      return () => {
        map.off("styledata", forceUpdate)
        // @ts-ignore
        if (map.style && map.style._loaded && map.getSource(id)) {
          // Parent effects are destroyed before child ones, see
          // https://github.com/facebook/react/issues/16728
          // Source can only be removed after all child layers are removed
          const allLayers = map.getStyle()?.layers
          if (allLayers) {
            for (const layer of allLayers) {
              // @ts-ignore (2339) source does not exist on all layer types
              if (layer.source === id) {
                map.removeLayer(layer.id)
              }
            }
          }
          map.removeSource(id)
        }
      }
    }
    return undefined
  }, [map])

  if (!map) return
  // @ts-ignore
  let source: Source | null | undefined = map && map.style && map.getSource(id)
  if (source) {
    updateSource(source, props, propsRef.current)
  } else {
    source = createSource(map, id, props)
  }
  propsRef.current = props

  return (
    (source &&
      React.Children.map(
        props.children,
        (child) =>
          child &&
          cloneElement(child, {
            source: id,
          }),
      )) ||
    null
  )
}
